import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarkIcon from '@mui/icons-material/Bookmark';

import PlaceAutocomplete from '../common/PlaceAutocomplete';
import { geocodePreciseAddress, hasCoordinates } from '../../utils/addressGeocoding';
import { VEHICLE_TYPE_OPTIONS, getVehicleTypeLabel } from '../../config/vehicleTypes';
import {
  fetchRideTemplates,
  saveRideTemplate,
  updateRideTemplate,
  deleteRideTemplate,
} from '../../redux/slices/ridesSlice';

const ORDER_TYPE_OPTIONS = [
  { value: '2', label: 'Sofortbestellung' },
  { value: '1', label: 'Vorbestellung' },
  { value: '3', label: 'Dauerauftrag' },
];

const orderTypeLabel = (value) =>
  ORDER_TYPE_OPTIONS.find((o) => o.value === String(value))?.label || 'Sofortbestellung';

const emptyForm = () => ({
  id: null,
  template_name: '',
  pickup_address: '',
  pickup_location: null,
  destination_address: '',
  destination_location: null,
  vehicle_type: '1',
  order_type: '2',
});

/**
 * Vorlagen-Verwaltung für einen einzelnen Kunden (in den Kundendetails).
 * Ersetzt die frühere eigenständige Vorlagen-Seite.
 */
const CustomerTemplatesSection = ({ customerId, customerName, customerPhone }) => {
  const dispatch = useDispatch();
  const templates = useSelector((state) => state.rides.templates);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchRideTemplates());
  }, [dispatch]);

  const customerTemplates = useMemo(
    () => (templates || []).filter((t) => t.customer_id === customerId),
    [templates, customerId]
  );

  const openCreate = () => {
    setForm(emptyForm());
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setForm({
      id: t.id,
      template_name: t.template_name || '',
      pickup_address: t.pickup_address || '',
      pickup_location: t.pickup_location || null,
      destination_address: t.destination_address || '',
      destination_location: t.destination_location || null,
      vehicle_type: String(t.vehicle_type || '1'),
      order_type: String(t.order_type || '2'),
    });
    setError(null);
    setDialogOpen(true);
  };

  const canSave = form.pickup_address.trim() && form.destination_address.trim() && form.vehicle_type;

  const handleSave = async () => {
    if (!canSave) {
      setError('Bitte Abhol- und Zieladresse sowie Fahrzeugtyp angeben.');
      return;
    }
    setSaving(true);
    setError(null);
    // Vorschlagspflicht (nextcab_mobile/docs/general/adressauswahl_konzept.md
    // §3): Aus der Vorlage entstehen Fahrten — getippte Adressen nur als genaue
    // Hausadresse, sonst Vorschlag oder Kartenpunkt.
    const locations = { pickup: form.pickup_location, destination: form.destination_location };
    for (const [key, label] of [['pickup', 'Abholadresse'], ['destination', 'Zieladresse']]) {
      if (hasCoordinates(locations[key])) continue;
      const precise = await geocodePreciseAddress(form[`${key}_address`]);
      if (!precise) {
        setError(`${label} ist nicht eindeutig — bitte einen Vorschlag aus der Liste wählen oder auf der Karte festlegen.`);
        setSaving(false);
        return;
      }
      locations[key] = { latitude: precise.latitude, longitude: precise.longitude };
    }
    const payload = {
      customer_id: customerId,
      customer_name: customerName || 'Kunde',
      customer_phone: customerPhone || '',
      template_name: form.template_name.trim() || undefined,
      pickup_address: form.pickup_address,
      pickup_location: locations.pickup,
      destination_address: form.destination_address,
      destination_location: locations.destination,
      vehicle_type: form.vehicle_type,
      order_type: form.order_type,
    };
    try {
      const result = form.id
        ? await dispatch(updateRideTemplate({ id: form.id, ...payload }))
        : await dispatch(saveRideTemplate(payload));
      if (!result.error) {
        setDialogOpen(false);
      } else {
        setError('Vorlage konnte nicht gespeichert werden.');
      }
    } catch (e) {
      setError('Vorlage konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await dispatch(deleteRideTemplate(deleteTarget.id));
    setDeleteTarget(null);
  };

  return (
    <Card elevation={3} sx={{ mb: 3, overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookmarkIcon fontSize="small" /> Vorlagen
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
            Neue Vorlage
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />

        {customerTemplates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Für diesen Kunden sind noch keine Vorlagen gespeichert.
          </Typography>
        ) : (
          <List dense disablePadding>
            {customerTemplates.map((t) => (
              <ListItem
                key={t.id}
                disableGutters
                secondaryAction={
                  <Box>
                    <Tooltip title="Bearbeiten">
                      <IconButton edge="end" size="small" onClick={() => openEdit(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton edge="end" size="small" color="error" onClick={() => setDeleteTarget(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemText
                  primary={t.template_name || `${t.pickup_address} → ${t.destination_address}`}
                  secondary={`${t.pickup_address} → ${t.destination_address} · ${getVehicleTypeLabel(parseInt(t.vehicle_type, 10))} · ${orderTypeLabel(t.order_type)}`}
                  secondaryTypographyProps={{ sx: { fontSize: '0.78rem' } }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</DialogTitle>
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Vorlagenname (optional)"
                value={form.template_name}
                onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
                size="small"
                fullWidth
              />
              <PlaceAutocomplete
                label="Abholadresse *"
                subDestinationRole="pickup"
                requireLocation
                location={form.pickup_location}
                value={form.pickup_address}
                // Nach einer Auswahl meldet das Element denselben Text noch einmal —
                // nur abweichender Text ist getippt und verwirft die Koordinate.
                onChange={(e) => setForm((f) => ({
                  ...f,
                  pickup_address: e.target.value,
                  pickup_location: e.target.value === f.pickup_address ? f.pickup_location : null,
                }))}
                onPlaceSelect={(addr, loc) => setForm((f) => ({ ...f, pickup_address: addr, pickup_location: loc }))}
                required
                size="small"
                fullWidth
              />
              <PlaceAutocomplete
                label="Zieladresse *"
                subDestinationRole="destination"
                requireLocation
                location={form.destination_location}
                value={form.destination_address}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  destination_address: e.target.value,
                  destination_location: e.target.value === f.destination_address ? f.destination_location : null,
                }))}
                onPlaceSelect={(addr, loc) => setForm((f) => ({ ...f, destination_address: addr, destination_location: loc }))}
                required
                size="small"
                fullWidth
              />
              <TextField
                select
                label="Fahrzeugtyp"
                value={form.vehicle_type}
                onChange={(e) => setForm((f) => ({ ...f, vehicle_type: e.target.value }))}
                size="small"
                fullWidth
              >
                {VEHICLE_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={String(o.value)}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Bestelltyp"
                value={form.order_type}
                onChange={(e) => setForm((f) => ({ ...f, order_type: e.target.value }))}
                size="small"
                fullWidth
              >
                {ORDER_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>
        </APIProvider>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !canSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Vorlage löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Soll die Vorlage „{deleteTarget?.template_name || `${deleteTarget?.pickup_address} → ${deleteTarget?.destination_address}`}" wirklich gelöscht werden?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Löschen</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CustomerTemplatesSection;
